import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { paint, unpaint, overlapping, selectionRange, type Mark } from './marks';
import { marksFor, rememberMarks } from './mark-store';

/**
 * Text a candidate may highlight and write notes on.
 *
 * The real test offers both on everything readable — the passage, the
 * questions, the notes a Listening paper is completed from — and a candidate
 * uses them to keep their place in something they are hearing once. So this
 * wraps whatever it is given rather than living inside the Reading passage.
 *
 * Selecting text raises the two buttons the real screen raises, and pressing
 * "h" does the same as clicking Highlight. Marks are held per pane and painted
 * back after every render: the pane redraws on every keystroke elsewhere, and
 * a highlight that vanished when a candidate typed an answer would be worse
 * than none.
 */

/**
 * Mark ids, unique across reloads.
 *
 * A bare counter restarted at 1 when the page reloaded, while the marks it had
 * handed out came back from storage still numbered from 1 — so a new highlight
 * could share an id with an old one, and taking one off took both.
 */
let sequence = 0;
const nextId = () => `m${Date.now().toString(36)}${(sequence += 1).toString(36)}`;

export function Markable({
  storageKey,
  children,
}: {
  /** Marks are kept per pane and per part, and do not follow one to another. */
  storageKey: string;
  children: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [marks, setMarks] = useState<Mark[]>(() => marksFor(storageKey));
  const [menu, setMenu] = useState<{ x: number; y: number; start: number; end: number } | null>(
    null,
  );
  const [noting, setNoting] = useState<{ start: number; end: number } | null>(null);
  const [draft, setDraft] = useState('');

  /**
   * The marks, in a ref as well as in state.
   *
   * Writing them through to the store has to happen where they change rather
   * than in an effect watching them: when a candidate moves to another part,
   * both the key and the marks change, and an effect would run with the new
   * key and the previous render's marks — filing passage one's highlights
   * under passage two.
   */
  const marksRef = useRef<Mark[]>(marks);

  const apply = useCallback(
    (next: Mark[], key: string) => {
      marksRef.current = next;
      setMarks(next);
      rememberMarks(key, next);
    },
    [],
  );

  /*
   * A different part, or the other pane: its marks are not these.
   *
   * They are fetched rather than dropped. This used to set an empty list, so
   * every highlight a candidate had drawn vanished the moment they looked at
   * another passage — and they had no way to get it back.
   */
  useEffect(() => {
    const held = marksFor(storageKey);
    marksRef.current = held;
    setMarks(held);
    setMenu(null);
    setNoting(null);
  }, [storageKey]);

  // Stripped and painted again after every render. Not additive on purpose:
  // taking a highlight off is then a matter of leaving it out of the next
  // coat, which is the only way React and a hand-written <mark> can agree.
  useEffect(() => {
    if (!host.current) return;
    unpaint(host.current);
    paint(host.current, marks);
  });

  const offer = useCallback((e?: React.MouseEvent) => {
    const root = host.current;
    if (!root) return;
    /*
     * A mouse released on the menu itself is not a new selection.
     *
     * The menu sits inside this container, so releasing the button over it
     * bubbled to here and re-read the selection — which the press had just
     * collapsed. That closed the menu, unmounting the very button being
     * pressed, and the click then had nothing to land on: Clear did nothing
     * at all, and no amount of pressing it helped.
     */
    if ((e?.target as HTMLElement | null)?.closest?.('.xmarkmenu, .xnote')) return;
    const range = selectionRange(root);
    if (!range) {
      setMenu(null);
      return;
    }
    const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) return;
    // Kept on the screen: a selection in the first line would otherwise put
    // the buttons above the top of the window, where they cannot be clicked.
    setMenu({
      x: Math.min(Math.max(rect.left + rect.width / 2, 70), window.innerWidth - 70),
      y: Math.max(rect.top, 64),
      ...range,
    });
  }, []);

  const add = useCallback(
    (note?: string) => {
      setMenu(null);
      setNoting(null);
      setDraft('');
      const span = noting ?? menu;
      if (span) {
        apply(
          [
            // A new mark replaces whatever it overlaps rather than nesting
            // inside it: two highlights over the same words are one highlight.
            ...marksRef.current.filter((m) => !overlapping([m], span.start, span.end).length),
            { id: nextId(), start: span.start, end: span.end, ...(note ? { note } : {}) },
          ],
          storageKey,
        );
      }
      window.getSelection()?.removeAllRanges();
    },
    [menu, noting, apply, storageKey],
  );

  /**
   * Clears every mark the selection touches.
   *
   * Clicking a highlight takes it off, but not on an option, where a click is
   * how the question gets answered — so without this a candidate could mark an
   * option and have no way at all to unmark it. Selecting the words again and
   * pressing Clear is the way out, and it works the same everywhere rather
   * than only where the click is free.
   */
  const clear = useCallback(() => {
    const span = menu;
    setMenu(null);
    if (!span) return;
    const doomed = new Set(overlapping(marksRef.current, span.start, span.end).map((m) => m.id));
    apply(
      marksRef.current.filter((m) => !doomed.has(m.id)),
      storageKey,
    );
    window.getSelection()?.removeAllRanges();
  }, [menu, apply, storageKey]);

  /** Whether the selection sits on anything already marked. */
  const onMarked = menu ? overlapping(marks, menu.start, menu.end).length > 0 : false;

  // "h" highlights, as the real screen's own tooltip says it does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!menu || e.key !== 'h') return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      e.preventDefault();
      add();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menu, add]);

  /** Clicking a mark takes it off, which is the only way the real one clears. */
  const onClick = (e: React.MouseEvent) => {
    const hit = (e.target as HTMLElement).closest('.xmark');
    if (!hit) return;
    /*
     * Unless the mark is on something that answers the question.
     *
     * Now that an option's own words can be highlighted, the text a candidate
     * clicks to choose an answer may be inside a <mark>. Taking the highlight
     * off there would mean a candidate who marked the option they were
     * weighing had to clear that marking to choose it — and the click they
     * meant as an answer would look like it did nothing else.
     */
    if (hit.closest('label, button, a, input, textarea, select')) return;
    const id = (hit as HTMLElement).dataset.mark;
    apply(
      marksRef.current.filter((m) => m.id !== id),
      storageKey,
    );
  };

  return (
    <div className="markable" ref={host} onMouseUp={(e) => offer(e)} onClick={onClick}>
      {children}

      {menu && !noting && (
        <div
          className="xmarkmenu"
          style={{ left: `${menu.x}px`, top: `${menu.y}px` }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            className="xmarkmenu__btn"
            onClick={() => {
              setNoting({ start: menu.start, end: menu.end });
              setMenu(null);
            }}
          >
            <span aria-hidden="true">❝</span>
            Note
          </button>
          {onMarked && (
            <button className="xmarkmenu__btn" onClick={clear} title="Remove the highlight">
              <span aria-hidden="true">✕</span>
              Clear
            </button>
          )}
          <button className="xmarkmenu__btn" onClick={() => add()} title="Highlight (h)">
            <span aria-hidden="true" className="xmarkmenu__rule" />
            Highlight
          </button>
        </div>
      )}

      {noting && (
        <div className="xnote" onMouseDown={(e) => e.stopPropagation()}>
          <label className="sr-only" htmlFor="note-text">
            Your note
          </label>
          <textarea
            id="note-text"
            className="xinput"
            rows={3}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Your note"
          />
          <div className="xnote__actions">
            <button className="xbtn xbtn--primary" onClick={() => add(draft.trim() || undefined)}>
              Save
            </button>
            <button
              className="xbtn"
              onClick={() => {
                setNoting(null);
                setDraft('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
